import { useRef, useState, useEffect } from 'react';
import { useCodeBlockEditorContext } from '@mdxeditor/editor';
import mermaid from 'mermaid';
import {
    MermaidErrorBox,
    MermaidDiagramContainer,
    MermaidClickable,
    MermaidEditWrapper,
    MermaidEditTextarea
} from './styled';

mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    themeVariables: {
        primaryColor: '#FABB18',
        primaryTextColor: '#fff',
        primaryBorderColor: '#26262B',
        lineColor: '#FABB18',
        secondaryColor: '#292B36',
        tertiaryColor: '#1E202A',
        background: '#1E202A',
        mainBkg: '#292B36',
        nodeBorder: '#FABB18',
        clusterBkg: '#1E202A',
        titleColor: '#fff',
        edgeLabelBackground: '#292B36'
    }
});

export const MermaidPreview = ({ code }) => {
    const containerRef = useRef(null);
    const [svg, setSvg] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!code) return;
        let cancelled = false;
        // Mermaid's render() requires a unique DOM element ID to mount the
        // temporary SVG it produces. Using Date.now() + random prevents
        // collisions when multiple diagrams render in the same tick.
        const id = `mermaid-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 7)}`;
        mermaid
            .render(id, code)
            .then((result) => {
                if (!cancelled) {
                    setSvg(result.svg);
                    setError(null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message || 'Invalid mermaid syntax');
                    setSvg('');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [code]);

    if (error) {
        return <MermaidErrorBox>Mermaid error: {error}</MermaidErrorBox>;
    }

    return (
        <MermaidDiagramContainer
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};

// Editable mermaid block: shows the rendered diagram by default,
// switches to a raw textarea on click so the user can edit the source.
export const MermaidCodeBlockEditor = ({ code }) => {
    const { setCode } = useCodeBlockEditorContext();
    const [isEditing, setIsEditing] = useState(false);

    if (isEditing) {
        return (
            <MermaidEditWrapper>
                <MermaidEditTextarea
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onBlur={() => setIsEditing(false)}
                    autoFocus
                />
            </MermaidEditWrapper>
        );
    }

    return (
        <MermaidClickable
            onClick={() => setIsEditing(true)}
            title="Click to edit Mermaid source"
        >
            <MermaidPreview code={code} />
        </MermaidClickable>
    );
};

// Read-only variant: just renders the diagram, no edit affordances.
const MermaidReadOnly = ({ code }) => {
    useCodeBlockEditorContext(); // required by MDXEditor for all block descriptors
    return <MermaidPreview code={code} />;
};

export const mermaidCodeBlockDescriptor = {
    priority: 100,
    match: (language) => language === 'mermaid',
    Editor: MermaidCodeBlockEditor
};

export const mermaidReadOnlyDescriptor = {
    priority: 100,
    match: (language) => language === 'mermaid',
    Editor: MermaidReadOnly
};
